import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useModal from '@tenant/utility/hooks/useModal';
import DeleteDialog from '@tenant/core/App/Dialog/DeleteDialog';
import PipelineRunForm from '../Common/PipelineRunForm';
import useCancelPipelineRun from 'redux/slices/pipelines/hooks/useCancelPipelineRun';
import queryKeys from 'redux/slices/pipelines/constants/queryKeys';
import { isCancellablePipelineRunStatus } from 'redux/slices/pipelines/constants/pipelineRunStatus';
import { mapCancelRunError } from 'redux/slices/pipelines/helpers/mapCancelRunError';

// AC-16 / R4 (pre-impl review) — the detail page's Run action. Unlike the list's old
// per-row Run dialog (now removed — it navigated to RUN_VIEW on trigger),
// this one stays on the detail page: closing the dialog is the ONLY side effect of a
// successful trigger. The page's own latest-run resolution (useGetListPipelineRuns(id)
// -> runs[0]) picks up the newly-created run automatically because useRunPipeline's
// onSuccess already invalidates the `getRuns(id)` query — no extra invalidation needed
// here. The single poller (PipelineRunStatusView, rendered by the parent flow) then
// re-mounts against the new runId and starts polling it in place.
//
// The git PAT stays request-scoped inside PipelineRunForm — never persisted, pre-filled,
// logged, or cached (NFR-Security) — re-mounted fresh (key=pipelineId) every time the
// dialog opens, mirroring PipelineRunDialog.
//
// Hidden entirely without PIPELINE_RUN; disabled while the latest run is non-terminal
// (`disabled` prop, computed by the parent from the currently displayed run).
//
// deploy-pipelines-run-cancel (Wave 5) adds the Cancel action beside Run:
//   - AC-17 — rendered ONLY while the displayed run's status is Queued/Building, derived from
//     the explicit `isCancellablePipelineRunStatus` ALLOW-list (never a negated terminal check),
//     so a FUTURE status is absent-by-default rather than accidentally cancellable. For
//     Deploying and every terminal status (including Cancelled itself) the action is ABSENT
//     from the DOM — not merely disabled: the deploy leg has no cancel seam at all (spec's
//     Design section), so a disabled-but-present button would assert a capability that does
//     not exist.
//   - AC-18 — gated on the SAME `useAllowedAccess([PIPELINE_RUN])` check as Run, above.
//   - AC-20/OQ-C — a lightweight confirmation before the request is sent, reusing the SAME
//     confirm-dialog component this page already hosts for Delete
//     (`@tenant/core/App/Dialog/DeleteDialog`) — explicitly NOT PipelineRunForm: cancel needs
//     no credential, so it collects none.
//   - AC-21/TC-DPRC-017 — a 409 (the run moved on between the last poll and this click, the
//     single most likely real-world failure of this feature) is distinguished from a generic
//     error, surfaced via toast, and the runs query is invalidated so the page's data catches
//     up rather than leaving a stale Cancel button behind; the already-running poller
//     (PipelineRunStatusView, unaffected here — no second poller is introduced) observes the
//     actual current status on its next tick.
const PipelineRunAction = ({ pipelineId, disabled, effectiveBranch, runId, runStatus }) => {
	const canRun = useAllowedAccess([AuthorizePermission.PIPELINE_RUN]);
	const { visible, onShow, onClose } = useModal();
	const cancelModal = useModal();
	const queryClient = useQueryClient();
	const cancelMutate = useCancelPipelineRun();

	if (!canRun) {
		return null;
	}

	const canCancel = !!runId && isCancellablePipelineRunStatus(runStatus);

	const handleCancel = () => {
		cancelMutate.mutate(
			{ id: pipelineId, runId },
			{
				onSuccess: () => {
					cancelModal.onClose();
				},
				onError: error => {
					cancelModal.onClose();
					// AC-21 — refresh the page's run data now rather than waiting out the ~4s poll
					// tick; the existing poller still owns observing the live `run.Status` transition
					// (no second poller is added here).
					queryClient.invalidateQueries({ queryKey: queryKeys.getRuns(pipelineId) });
					toast.error(mapCancelRunError(error));
				}
			}
		);
	};

	return (
		<>
			{/* AC-15/OQ-1d — Run is the single highest-emphasis action on the page (theme
			    `primary`, via `variant="contained"`), never a hex. AC-15a — the label is always
			    `Run`, never `Retry deploy`: POST /run always executes build then deploy, so a
			    deploy-only "retry" label would assert a capability the engine does not have. */}
			<Button variant="contained" disabled={disabled} onClick={onShow}>
				{disabled ? 'Run (in progress)' : 'Run'}
			</Button>

			{/* AC-17 — present only while cancellable; absent (not disabled) otherwise. Secondary
			    emphasis (outlined) — Run stays the single highest-emphasis action on the page. */}
			{canCancel && (
				<Button variant="outlined" color="secondary" onClick={cancelModal.onShow} aria-label="Cancel run">
					Cancel
				</Button>
			)}

			<Dialog open={visible} onClose={onClose} maxWidth="sm" fullWidth>
				<DialogTitle>Run Pipeline</DialogTitle>
				<DialogContent>
					{/* Re-mounted fresh every time the dialog opens (key=pipelineId) so the PAT
              field is guaranteed empty — never carries a value from a previous open. */}
					{visible && (
						<PipelineRunForm key={pipelineId} pipelineId={pipelineId} onTriggered={onClose} effectiveBranch={effectiveBranch} />
					)}
				</DialogContent>
			</Dialog>

			{/* AC-20/OQ-C — a PLAIN confirm collecting no PAT and no input, explicitly not
			    PipelineRunForm. Reuses the same confirm-dialog component the Delete action on this
			    page already hosts, focus-managed by MUI's Dialog. */}
			{/* `action="Cancel run"`, not "Cancel": DeleteDialog labels its own DISMISS button
			    "Cancel" unconditionally, so `action="Cancel"` would render two identically-named
			    buttons in one dialog — indistinguishable to a screen reader and ambiguous to
			    everyone else (NFR-Accessibility). "Cancel run" also makes the title read
			    "Confirm Cancel run". */}
			<DeleteDialog visible={cancelModal.visible} loading={cancelMutate.isPending} action="Cancel run" handleClose={cancelModal.onClose} handleSave={handleCancel}>
				<DialogContentText>This will stop the run immediately. It cannot be resumed or undone — the next run will be a new run.</DialogContentText>
			</DeleteDialog>
		</>
	);
};

export default React.memo(PipelineRunAction);

PipelineRunAction.propTypes = {
	pipelineId: PropTypes.string.isRequired,
	disabled: PropTypes.bool,
	// deploy-pipelines-run-overrides (Wave 4) — threaded straight through to PipelineRunForm; see
	// that component's propTypes comment for provenance (data the parent already fetched).
	effectiveBranch: PropTypes.string,
	// deploy-pipelines-run-cancel (Wave 5) — the displayed run's id/status, threaded down from
	// the SAME `displayRun` PipelineDetail already resolves (no new fetch, no second poller).
	// Both are required for Cancel to render at all (AC-17).
	runId: PropTypes.string,
	runStatus: PropTypes.number
};
